import StyledMenu from '@/components/StyledMenu/StyledMenu.tsx';
import { Group, Menu } from '@mantine/core';
import StyledButton from '@/components/StyledButton/StyledButton.tsx';
import {
  HiArrowDownTray,
  HiArrowPathRoundedSquare,
  HiMiniEllipsisVertical,
  HiOutlineArrowPath,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineXCircle,
} from 'react-icons/hi2';
import useCancelRunExecutions from '@/hooks/api/runs/useCancelRunExecutions.ts';
import { ReactNode, useState } from 'react';
import DeleteRunModal from '@/components/DeleteRunModal.tsx';
import useDownloadRunFilesZip, { RunFilesType } from '@/hooks/api/runs/useDownloadRunFilesZip.ts';
import { ActiveStatuses, ExecutionStatus } from '@/types/statuses.ts';
import { getRunDisplayName, Run } from '@/types/runs.ts';
import useReprocessInputsAndCreateNewRun from '@/hooks/api/runs/rerun/useReprocessInputsAndCreateNewRun.ts';
import { RERUN_STEP_CUTOFF } from '@/utils/constants.ts';
import RerunFailedStepsModal from '@/components/RerunFailedStepsModal.tsx';

export const RUN_MENU_TRIGGER_SIZE = 25;

type props = {
  run: Run;
  visible?: boolean;
  loading?: boolean;
  onRename?: () => void;
  downloadFilesType?: RunFilesType;
  extraMenuItems?: ReactNode;
  postRerunMenuItems?: ReactNode;
  withinPortal?: boolean;
  children?: ReactNode;
  triggerIcon?: ReactNode;
  triggerSize?: number;
};

const RunMenu = ({
  run,
  visible = true,
  loading: externalLoading = false,
  onRename,
  downloadFilesType,
  extraMenuItems,
  postRerunMenuItems,
  withinPortal = true,
  children,
  triggerIcon = <HiMiniEllipsisVertical size={15} color="#BABABA" />,
  triggerSize = RUN_MENU_TRIGGER_SIZE,
}: props) => {
  const { mutate: cancelRun, isPending: cancellingRun } = useCancelRunExecutions({});
  const [deleteRun, setDeleteRun] = useState<Run | null>(null);
  const [showRerunFailedSteps, setShowRerunFailedSteps] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const triggerAriaLabel = `${getRunDisplayName(run)} options`;

  const { mutate: downloadFiles, isPending: downloadingFiles } = useDownloadRunFilesZip(run);

  const { mutate: reprocessRun, isPending: isReprocessing } = useReprocessInputsAndCreateNewRun();

  const runIsActive = ActiveStatuses.includes(run.status);
  const runTooOldForRerun = RERUN_STEP_CUTOFF >= new Date(run.created_at);
  const runHasRerunnableFailure = [
    ExecutionStatus.AskUser,
    ExecutionStatus.Errors,
    ExecutionStatus.PartiallyCompleted,
  ].includes(run.status);
  const canRerunFailedSteps = runHasRerunnableFailure && !runTooOldForRerun;

  const loading = externalLoading || cancellingRun || downloadingFiles || isReprocessing;

  const cancellable = ![
    ExecutionStatus.Cancelled,
    ExecutionStatus.Errors,
    ExecutionStatus.PartiallyCompleted,
    ExecutionStatus.Completed,
  ].includes(run.status);

  if (!visible && !showMenu && !loading && !deleteRun && !showRerunFailedSteps) {
    return <>{children}</>;
  }

  return (
    <Group
      gap={0}
      wrap="nowrap"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <StyledMenu
        position="bottom-start"
        opened={showMenu}
        onChange={setShowMenu}
        withinPortal={withinPortal}
        withOverlay={true}
        overlayProps={{ opacity: 0 }}
      >
        <Menu.Target>
          <span>
            <StyledButton
              size={'sm'}
              variant={'subtleIcon'}
              loading={loading}
              aria-label={triggerAriaLabel}
              style={{ width: triggerSize, height: triggerSize }}
            >
              {triggerIcon}
            </StyledButton>
          </span>
        </Menu.Target>
        <Menu.Dropdown>
          {cancellable && (
            <Menu.Item
              onClick={() => cancelRun({ run_id: run.id })}
              leftSection={<HiOutlineXCircle size={14} style={{ marginRight: '-4px' }} />}
            >
              Cancel
            </Menu.Item>
          )}
          {onRename !== undefined && (
            <Menu.Item
              onClick={onRename}
              leftSection={<HiOutlinePencil size={14} style={{ marginRight: '-4px' }} />}
            >
              Rename
            </Menu.Item>
          )}

          {downloadFilesType && (
            <Menu.Item
              onClick={() => downloadFiles({ filesType: downloadFilesType })}
              leftSection={<HiArrowDownTray size={14} style={{ marginRight: '-4px' }} />}
            >
              Download{' ' + downloadFilesType}
            </Menu.Item>
          )}
          {extraMenuItems}

          <Menu.Item
            onClick={() => setDeleteRun(run)}
            leftSection={<HiOutlineTrash size={14} style={{ marginRight: '-4px' }} />}
          >
            Delete
          </Menu.Item>
          {canRerunFailedSteps && (
            <Menu.Item
              onClick={() => setShowRerunFailedSteps(true)}
              leftSection={<HiArrowPathRoundedSquare size={14} style={{ marginRight: '-4px' }} />}
            >
              Rerun failed or blocked steps
            </Menu.Item>
          )}
          {!runIsActive && (
            <Menu.Item
              onClick={() => reprocessRun({ runId: run.id })}
              leftSection={<HiOutlineArrowPath size={14} style={{ marginRight: '-4px' }} />}
            >
              Reprocess and Rerun
            </Menu.Item>
          )}
          {postRerunMenuItems}
        </Menu.Dropdown>
      </StyledMenu>
      {deleteRun && <DeleteRunModal run={deleteRun} onClose={() => setDeleteRun(null)} />}
      <RerunFailedStepsModal
        run={run}
        opened={showRerunFailedSteps}
        onClose={() => setShowRerunFailedSteps(false)}
      />
    </Group>
  );
};

export default RunMenu;
